import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBan, faClose } from '@fortawesome/pro-duotone-svg-icons';
import { faUserCheck } from '@fortawesome/pro-solid-svg-icons';
import { Fieldset } from '../../../../shared/components/ui/fieldset/fieldset';
import { IconBtn } from '../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { ModerationService } from '../../../../core/services/moderation.service';
import { BanEntry } from '../../../../shared/schemas/backend-api';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';

/**
 * Lists the domain's active (domain-wide) bans and lets a moderator lift any of
 * them. Only rendered when the own user holds the `kickUser` permission — that
 * gating lives in the parent `GeneralSettings` (the same permission
 * also guards the server endpoints this calls).
 */
@Component({
  selector: 'andor-settings-bans',
  standalone: true,
  imports: [
    LocalizedDatePipe,
    TranslatePipe,
    FaIconComponent,
    FaDuotoneIconComponent,
    Fieldset,
    IconBtn,
  ],
  templateUrl: './settings-bans.html',
  styleUrl: './settings-bans.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsBans {
  protected readonly faBan = faBan;
  protected readonly faClose = faClose;
  protected readonly faUserCheck = faUserCheck;

  bans = signal<BanEntry[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  /** userId currently being lifted, so its row can show a pending state. */
  removing = signal<string | null>(null);

  constructor(
    private moderationService: ModerationService,
    private translate: TranslateService
  ) {
    this.load();
  }

  async load() {
    this.loading.set(true);
    const bans = await this.moderationService.listBans();
    this.bans.set([...bans].sort((a, b) => a.bannedUntil - b.bannedUntil));
    this.loading.set(false);
  }

  async removeBan(ban: BanEntry) {
    if (this.removing()) return;
    this.removing.set(ban.userId);
    const ok = await this.moderationService.removeBan(ban.userId);
    if (ok) {
      this.bans.update((list) => list.filter((b) => b.userId !== ban.userId));
    } else {
      this.setError(this.translate.instant('Could not lift the ban. Please try again.'));
    }
    this.removing.set(null);
  }

  /**
   * Coarse "~Nd" hint shown next to the absolute (locale-formatted) date. Kept
   * as language-neutral unit abbreviations so it renders correctly without a
   * dictionary entry; the absolute date carries the locale-aware formatting.
   */
  relativeExpiry(bannedUntil: number): string {
    const ms = bannedUntil - Date.now();
    if (ms <= 0) return this.translate.instant('soon');
    const days = Math.round(ms / 86_400_000);
    if (days >= 365) return `~${Math.round(days / 365)}y`;
    if (days >= 1) return `~${days}d`;
    const hours = Math.max(1, Math.round(ms / 3_600_000));
    return `~${hours}h`;
  }

  private setError(text: string) {
    this.error.set(text);
    setTimeout(() => this.error.set(null), 5000);
  }
}
